import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { Card, CardContent, CardHeader } from '@/components/ui/card';
import useBrains from '@/hooks/useBrains';
import useCollections from '@/hooks/useCollections';
import { useDeskId } from '@/hooks/useDeskId';
import { useIntegrations } from '@/hooks/useIntegrations';
import useVersions from '@/hooks/useVersions';
import useWebhooks from '@/hooks/useWebhooks';
import useLanguageModels from '@/modules/developerTools/hooks/useLanguageModels';
import { ProviderName } from '@/modules/developerTools/types';
import { selectAccountSlug, selectBrainId } from '@/redux/session/selectors';
import { IntegrationStatus } from '@/shared/types/integration';

import {
  AiAgentBox,
  ChannelsBox,
  ConversationsBox,
  Divider,
  KnowledgeBox,
  PersonalizationBox,
} from './OverviewFlowComponents';

// Refresh interval for the overview flow so we can see the latest active sessions
const REFRESH_INTERVAL_THIRTY_SECONDS = 30000;

export const OverviewFlow = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const brainId = useSelector(selectBrainId);
  const slug = useSelector(selectAccountSlug);
  const { brain, updateBrain } = useBrains(brainId);
  const { collectionsByLanguage } = useCollections();
  const deskId = useDeskId();
  const { integrations } = useIntegrations(deskId);
  const { activeSessions } = useVersions({
    brainId,
    refetchInterval: REFRESH_INTERVAL_THIRTY_SECONDS,
  });
  const { webhooks } = useWebhooks(brainId);
  const { languageModels } = useLanguageModels();
  const { collection_id, language_model_id, language } = brain || {};

  // Get collections filtered by brain's language
  const collections = useMemo(
    () => collectionsByLanguage(language) || [],
    [collectionsByLanguage, language]
  );

  // Find the provider for the current language model
  const languageModelProvider = useMemo((): ProviderName => {
    if (!language_model_id) {
      return 'moveo';
    }
    const languageModel = languageModels.find((lm) => lm.language_model_id === language_model_id);
    return languageModel?.provider ?? 'moveo';
  }, [language_model_id, languageModels]);

  // Find first_message webhook
  const firstMessageWebhook = webhooks?.find((webhook) => webhook.type === 'first_message');

  // Get all active and verified integrations across all desks
  const activeChannels = useMemo(() => {
    if (!integrations) {
      return [];
    }

    return integrations.filter(
      (integration) => integration.active && integration?.status === IntegrationStatus.VERIFIED
    );
  }, [integrations]);

  // Get current knowledge base
  const currentCollection = collections?.find((col) => col.collection_id === collection_id);

  const personalizationLabel = t('ai_agents.overview.personalization_data');
  const knowledgeLabel = t('ai_agents.navigation.knowledge');
  const aiAgentLabel = t('common.ai_agent');
  const channelsLabel = t('common.connections');
  const conversationsLabel = t('ai_agents.overview.conversations');
  const hasWebhook = !!firstMessageWebhook;
  const hasChannels = activeChannels.length > 0;
  const personalizationTooltip = t('ai_agents.overview.go_to_personalization_webhook');
  const channelsTooltip = hasChannels
    ? `${t('common.go_to')} ${t('common.connections')}`
    : t('ai_agents.overview.add_connection');
  const aiAgentTooltip = `${t('common.go_to')} ${t('common.settings')}`;

  const handlePersonalizationClick = () => {
    if (firstMessageWebhook) {
      navigate(
        `/${slug}/ai-agents/${brainId}/workflows/webhooks/${firstMessageWebhook.webhook_id}`
      );
    } else {
      navigate(`/${slug}/ai-agents/${brainId}/workflows/webhooks/new?type=first_message`);
    }
  };

  const handleCollectionChange = (collectionId: string | null) => {
    if (brainId) {
      updateBrain({ brain_id: brainId, collection_id: collectionId });
    }
  };

  const handleChannelsClick = () => {
    navigate(`/${slug}/environments/${deskId}/integrations`);
  };

  const handleAiAgentClick = () => {
    navigate(`/${slug}/ai-agents/${brainId}/settings`);
  };

  return (
    <Card className="w-full gap-0 rounded-md border-border bg-background p-0 text-foreground shadow-none">
      <CardHeader className="p-5 pb-3">
        <span className="text-base leading-6 font-semibold">{t('common.overview')}</span>
      </CardHeader>
      <CardContent className="px-5 pb-5">
        <div className="@container">
          {/* Small container layout: 2 rows */}
          <div className="grid grid-cols-[120px_minmax(20px,1fr)_120px_minmax(20px,1fr)_120px] items-center @[750px]:hidden">
            {/* Row 1: Personalization, Arrow, Knowledge, Arrow, AI Agent */}
            <PersonalizationBox
              label={personalizationLabel}
              hasWebhook={hasWebhook}
              tooltip={personalizationTooltip}
              onClick={handlePersonalizationClick}
            />
            <Divider />
            <KnowledgeBox
              label={knowledgeLabel}
              collection={currentCollection}
              collections={collections}
              onCollectionChange={handleCollectionChange}
            />
            <Divider />
            <AiAgentBox
              label={aiAgentLabel}
              provider={languageModelProvider}
              tooltip={aiAgentTooltip}
              onClick={handleAiAgentClick}
            />

            {/* Row 2: Channels, Arrow, Conversations */}
            <ChannelsBox
              label={channelsLabel}
              channels={activeChannels}
              tooltip={channelsTooltip}
              onClick={handleChannelsClick}
            />
            <Divider />
            <ConversationsBox label={conversationsLabel} count={activeSessions || 0} />
          </div>

          {/* Large container layout: 1 row */}
          <div className="hidden grid-cols-[120px_minmax(20px,1fr)_120px_minmax(20px,1fr)_120px_minmax(20px,1fr)_120px_minmax(20px,1fr)_120px] items-center @[750px]:grid">
            <PersonalizationBox
              label={personalizationLabel}
              hasWebhook={hasWebhook}
              tooltip={personalizationTooltip}
              onClick={handlePersonalizationClick}
            />
            <Divider />
            <KnowledgeBox
              label={knowledgeLabel}
              collection={currentCollection}
              collections={collections}
              onCollectionChange={handleCollectionChange}
            />
            <Divider />
            <AiAgentBox
              label={aiAgentLabel}
              provider={languageModelProvider}
              tooltip={aiAgentTooltip}
              onClick={handleAiAgentClick}
            />
            <Divider />
            <ChannelsBox
              label={channelsLabel}
              channels={activeChannels}
              tooltip={channelsTooltip}
              onClick={handleChannelsClick}
            />
            <Divider />
            <ConversationsBox label={conversationsLabel} count={activeSessions || 0} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
